/**
 * SchedulerHealthCard — Answers "is the coverage scheduler actually running?"
 *
 * The sibling SchedulerToggle surfaces the DB-configured state (businessHours /
 * offHours flags). This card surfaces the *runtime* liveness of the cron job on
 * the backend — the gap that caused the April 2026 incident where the DB said
 * enabled:true but the scheduler had never been instantiated at boot.
 *
 * Key signals:
 *   - /api/coverage/scheduler/status.enabled      runtime (cronJob !== null)
 *   - /api/coverage/scheduler/status.config.enabled  DB-configured
 *   - /api/coverage/scheduler/status.stats.lastTick  proves tick is firing
 *   - /api/coverage/stats.pending_windows         backlog size
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, CheckCircle, Clock, Loader2, Power, Zap,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SchedulerHealthCard.css';

function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmt(n) { return Number(n || 0).toLocaleString(); }

export default function SchedulerHealthCard() {
  const { data: status, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['scheduler', 'health-status'],
    queryFn: () => apiClient.get('/api/coverage/scheduler/status'),
    refetchInterval: 15000,
  });

  const { data: stats } = useQuery({
    queryKey: ['coverage', 'stats'],
    queryFn: () => apiClient.get('/api/coverage/stats'),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="schcard schcard--loading">
        <Loader2 size={16} className="schcard-spin" />
        <span>Loading scheduler health…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schcard schcard--error">
        <AlertTriangle size={16} />
        <span>Scheduler status unreachable: {error.message}</span>
      </div>
    );
  }

  const runtimeEnabled = !!status?.enabled;
  const configEnabled = status?.config?.enabled !== false;
  const anyHoursEnabled = !!(status?.config?.businessHours || status?.config?.offHours);
  const throttleBlocked = !!status?.throttle?.blocked;
  const allowedNow = !!status?.isAllowedTime;
  const currentlyRunning = !!status?.running;
  const lastTick = status?.stats?.lastTick;
  const lastJobQueued = status?.stats?.lastJobQueued;
  const tickAgeMs = lastTick ? Date.now() - new Date(lastTick).getTime() : null;
  const tickIsStale = tickAgeMs !== null && tickAgeMs > 5 * 60 * 1000; // >5 min since last tick
  const tickNever = !lastTick;

  // Overall state
  let state, stateLabel, StateIcon;
  if (!configEnabled || !anyHoursEnabled) {
    state = 'disabled';
    stateLabel = 'DISABLED';
    StateIcon = Power;
  } else if (!runtimeEnabled) {
    // config says on but runtime is off — the drift bug
    state = 'drift';
    stateLabel = 'NOT RUNNING';
    StateIcon = AlertTriangle;
  } else if (throttleBlocked) {
    state = 'throttled';
    stateLabel = 'THROTTLED';
    StateIcon = AlertTriangle;
  } else if (!allowedNow) {
    state = 'waiting';
    stateLabel = 'WAITING FOR HOURS';
    StateIcon = Clock;
  } else if (tickNever || tickIsStale) {
    state = 'stalled';
    stateLabel = 'STALLED';
    StateIcon = AlertTriangle;
  } else if (currentlyRunning) {
    state = 'running';
    stateLabel = 'RUNNING (ticking now)';
    StateIcon = Zap;
  } else {
    state = 'running';
    stateLabel = 'RUNNING';
    StateIcon = CheckCircle;
  }

  const pendingWindows = stats?.pending_windows != null
    ? Number(stats.pending_windows)
    : null;

  // Nudge details — why the current state?
  const nudges = [];
  if (state === 'drift') {
    nudges.push('Config says enabled, but no cron job is ticking on the backend. A backend restart will pick up the DB flag.');
  }
  if (state === 'throttled' && status?.throttle?.blockedUntil) {
    const mins = Math.max(0, Math.ceil((new Date(status.throttle.blockedUntil) - Date.now()) / 60000));
    nudges.push(`CaleProcure block in effect (~${mins} min remaining).`);
  }
  if (state === 'stalled') {
    nudges.push('The scheduler is enabled but hasn\'t ticked recently. Check backend logs — the cron may have crashed.');
  }
  if (state === 'waiting') {
    nudges.push('Outside configured allowed hours — flip Business Hours or Off-Hours in the scheduler toggle to change.');
  }

  return (
    <div className={`schcard schcard--${state}`}>
      <div className="schcard-header">
        <div className="schcard-header-left">
          <Activity size={16} className="schcard-icon" />
          <span className="schcard-title">Coverage Scheduler</span>
        </div>
        <div className={`schcard-badge schcard-badge--${state}`}>
          <StateIcon size={12} />
          <span>{stateLabel}</span>
        </div>
      </div>

      {nudges.length > 0 && (
        <div className="schcard-nudges">
          {nudges.map((n, i) => (
            <div key={i} className="schcard-nudge">
              <AlertTriangle size={12} />
              <span>{n}</span>
            </div>
          ))}
        </div>
      )}

      <div className="schcard-grid">
        <div className="schcard-metric">
          <span className="schcard-metric-label">Runtime</span>
          <span className={`schcard-metric-value ${runtimeEnabled ? 'is-good' : 'is-bad'}`}>
            {runtimeEnabled ? 'cron ticking' : 'cron offline'}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Config</span>
          <span className={`schcard-metric-value ${configEnabled && anyHoursEnabled ? 'is-good' : 'is-muted'}`}>
            {configEnabled
              ? (anyHoursEnabled
                  ? `${status.config.businessHours ? 'biz' : ''}${status.config.businessHours && status.config.offHours ? '+' : ''}${status.config.offHours ? 'off' : ''}`
                  : 'enabled, no hours')
              : 'disabled'}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last tick</span>
          <span className={`schcard-metric-value ${tickNever || tickIsStale ? 'is-bad' : ''}`}>
            {timeAgo(lastTick)}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last job queued</span>
          <span className="schcard-metric-value">{timeAgo(lastJobQueued)}</span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Jobs queued (session)</span>
          <span className="schcard-metric-value">{fmt(status?.stats?.jobsQueued)}</span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Pending windows</span>
          <span className={`schcard-metric-value ${pendingWindows > 0 ? 'is-warn' : ''}`}>
            {pendingWindows != null ? fmt(pendingWindows) : '—'}
          </span>
        </div>
        {status?.stats?.ticksSkippedHours > 0 && (
          <div className="schcard-metric">
            <span className="schcard-metric-label">Skipped (hours)</span>
            <span className="schcard-metric-value is-muted">{fmt(status.stats.ticksSkippedHours)}</span>
          </div>
        )}
        {status?.stats?.ticksSkippedThrottle > 0 && (
          <div className="schcard-metric">
            <span className="schcard-metric-label">Skipped (throttle)</span>
            <span className="schcard-metric-value is-warn">{fmt(status.stats.ticksSkippedThrottle)}</span>
          </div>
        )}
      </div>

      <div className="schcard-footer">
        Polling every 15s · last refreshed {timeAgo(new Date(dataUpdatedAt).toISOString())}
      </div>
    </div>
  );
}
